import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import SalaryNegotiationList from '@/components/negotiation/SalaryNegotiationList';
import PremiumLockModal from '@/components/subscription/PremiumLockModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { JobApiResponse, ScenarioInfo, SalaryNegotiationResponse, useApiClient } from '@/lib/api';
import { sortJobsByPriorityAndRecency } from '@/lib/job-status';
import { cn } from '@/lib/utils';
import { DollarSign, Lock, Play, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

// Category color mapping - hash-based generation for consistent, well-separated colors
// Improved algorithm to ensure better color separation
const getCategoryColor = (category: string): string => {
  // Generate a hash from the category name using djb2 algorithm
  let hash = 5381;
  for (let i = 0; i < category.length; i++) {
    hash = ((hash << 5) + hash) + category.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use golden ratio multiplier for better distribution across color spectrum
  // This ensures categories get well-separated hues
  const goldenRatio = 0.618033988749895;
  const hue = Math.abs((hash * goldenRatio) % 1) * 360;
  
  // Adjust saturation and lightness based on hash for additional variation
  // This prevents similar categories from looking too similar even if hue is close
  const saturationVariation = 65 + (Math.abs(hash % 20) - 10); // 55-75%
  const lightnessVariation = 45 + (Math.abs((hash >> 8) % 16) - 8); // 37-53%
  
  // Use calculated saturation and lightness for better distinction
  const saturation = Math.max(60, Math.min(75, saturationVariation));
  const lightness = Math.max(40, Math.min(55, lightnessVariation));
  
  return `hsl(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%)`;
};

export default function SalaryNegotiations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useApiClient();
  const { user } = useAuth();
  const isPremium = (user?.subscription_mode ?? 'free') === 'premium';
  
  const [jobs, setJobs] = useState<JobApiResponse[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<JobApiResponse | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [showPracticeMode, setShowPracticeMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filterJobId, setFilterJobId] = useState<string | null>(null);
  
  // Subscription and limit checking
  const [negotiations, setNegotiations] = useState<SalaryNegotiationResponse[]>([]);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    used: number;
    limit: number;
    windowEnd: string | null;
  } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle jobId from URL params
  useEffect(() => {
    const jobIdParam = searchParams.get('jobId');
    if (jobIdParam && jobs.length > 0) {
      const job = jobs.find(j => j.id === jobIdParam);
      if (job) {
        setSelectedJob(job);
        setShowPracticeMode(true);
      }
    }
  }, [searchParams, jobs]);

  // Initialize selected category to null (All) by default
  // No need to auto-select first category anymore

  const fetchData = async () => {
    try {
      setLoading(true);
      const [jobsData, scenariosData, negotiationsData] = await Promise.all([
        api.getJobs(),
        api.getScenarios(),
        api.getNegotiations().catch(() => [] as SalaryNegotiationResponse[]), // Fetch negotiations to check usage
      ]);
      setJobs(sortJobsByPriorityAndRecency(jobsData || []));
      setScenarios(scenariosData);
      setNegotiations(negotiationsData || []);
      
      // Check if user has used their weekly limit (for non-premium users only)
      if (!isPremium && negotiationsData && negotiationsData.length > 0) {
        const now = new Date();
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday of current week
        weekStart.setHours(0, 0, 0, 0);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);
        
        const weeklyNegotiations = negotiationsData.filter((n: SalaryNegotiationResponse) => {
          const created = new Date(n.created_at);
          return created >= weekStart && created < weekEnd;
        });
        
        if (weeklyNegotiations.length >= 1) {
          setRateLimitInfo({
            used: weeklyNegotiations.length,
            limit: 1,
            windowEnd: weekEnd.toISOString(),
          });
        } else {
          // Reset rate limit info if user hasn't used their limit
          setRateLimitInfo(null);
        }
      } else if (isPremium) {
        // Premium users have no limits - clear any rate limit info
        setRateLimitInfo(null);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartNegotiation = (job: JobApiResponse, scenarioId: string) => {
    // Check if user has reached their limit
    if (!isPremium && rateLimitInfo && rateLimitInfo.used >= rateLimitInfo.limit) {
      // Ensure rateLimitInfo is set for the modal
      if (!rateLimitInfo) {
        // Fallback: set default rate limit info if somehow missing
        const now = new Date();
        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() + (7 - ((now.getDay() + 6) % 7))); // Next Monday
        weekEnd.setHours(0, 0, 0, 0);
        setRateLimitInfo({
          used: 1,
          limit: 1,
          windowEnd: weekEnd.toISOString(),
        });
      }
      setShowPremiumModal(true);
      return;
    }
    
    if (scenarioId === 'custom') {
      navigate(`/jobs/${job.id}/negotiation?scenario=custom`);
    } else {
      navigate(`/jobs/${job.id}/negotiation?scenario=${scenarioId}`);
    }
  };
  
  const canCreateNegotiation = isPremium || !rateLimitInfo || rateLimitInfo.used < rateLimitInfo.limit;

  useEffect(() => {
    if (selectedJob && !selectedScenario) {
      // Auto-scroll to scenarios when job is selected
      setTimeout(() => {
        const element = document.getElementById('scenarios-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [selectedJob, selectedScenario]);


  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-8 py-4 sm:py-6 md:py-8 max-w-full overflow-x-hidden" style={{ boxSizing: 'border-box' }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Salary Negotiation
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Practice realistic salary negotiations and get detailed, actionable feedback on your approach.
            </p>
            {!isPremium && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Free Plan:</strong> {rateLimitInfo && rateLimitInfo.used >= rateLimitInfo.limit ? (
                    <>
                      You've used your weekly negotiation ({rateLimitInfo.used}/{rateLimitInfo.limit}).
                      {rateLimitInfo.windowEnd && (
                        <> Your limit resets on {new Date(rateLimitInfo.windowEnd).toLocaleDateString()}.</>
                      )}
                    </>
                  ) : (
                    <>You can create 1 salary negotiation per week. Upgrade to Premium for unlimited practice.</>
                  )}
                </p>
              </div>
            )}
          </div>
          <Button 
            onClick={() => setShowPracticeMode(!showPracticeMode)}
            variant={showPracticeMode ? "outline" : "default"}
            className={cn(
              "w-full sm:w-auto shadow-lg hover:shadow-xl transition-all",
              showPracticeMode 
                ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white border-0"
            )}
          >
            {showPracticeMode ? "Back" : "Practice Now"}
          </Button>
        </div>

        {/* Always show negotiations when not in practice mode */}
        {!showPracticeMode && (
          <>
            {/* Job Filter Dropdown */}
            {jobs.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="w-full sm:max-w-xs">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Filter by Job</label>
                    <Select
                      value={filterJobId || "all"}
                      onValueChange={(value) => setFilterJobId(value === "all" ? null : value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Jobs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Jobs</SelectItem>
                        {jobs.map((job) => (
                          <SelectItem key={job.id} value={job.id}>
                            {job.title ? `${job.title} - ${job.company}` : job.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Negotiations List */}
            <div className="-mx-4 sm:mx-0">
              {jobs.length > 0 ? (
                <SalaryNegotiationList jobId={filterJobId || undefined} />
              ) : (
              <Card className="text-center py-16 px-4 sm:px-8">
                <CardContent>
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                    <DollarSign className="w-12 h-12 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Ready to Practice Salary Negotiations?
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto text-base leading-relaxed">
                    Add some jobs you're interested in to practice salary negotiations and get detailed, actionable feedback on your approach.
                  </p>
                  <Button 
                    onClick={() => navigate('/jobs/new')}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all"
                    size="lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Your First Job
                  </Button>
                </CardContent>
              </Card>
              )}
            </div>
          </>
        )}


        {/* Scenario Selection - Only show in practice mode */}
        {showPracticeMode && (
          <div className="mb-8" id="scenarios-section">
            {selectedJob ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Select a Scenario for {selectedJob.title} at {selectedJob.company}
                </h2>
                <Button variant="outline" onClick={() => setSelectedJob(null)} className="w-full sm:w-auto">
                  Change Job
                </Button>
              </div>
            ) : (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select a Job</h2>
                {jobs.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                        <DollarSign className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Jobs Yet</h3>
                      <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Add a job first to practice salary negotiations.
                      </p>
                      <Button onClick={() => navigate('/jobs/new')}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Job
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Job Title</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {[...jobs]
                              .sort((a, b) => {
                                const aDate = new Date(a.created_at || a.updated_at || 0).getTime();
                                const bDate = new Date(b.created_at || b.updated_at || 0).getTime();
                                return bDate - aDate;
                              })
                              .map((job) => (
                                <TableRow
                                  key={job.id}
                                  onClick={() => {
                                    setSelectedJob(job);
                                    setTimeout(() => {
                                      const element = document.getElementById('scenarios-section');
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }
                                    }, 100);
                                  }}
                                  className="cursor-pointer hover:bg-gray-50"
                                >
                                  <TableCell className="font-medium">{job.title || 'Untitled'}</TableCell>
                                  <TableCell>{job.company || 'N/A'}</TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedJob(job);
                                        setTimeout(() => {
                                          const element = document.getElementById('scenarios-section');
                                          if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                          }
                                        }, 100);
                                      }}
                                    >
                                      Select
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {selectedJob && (() => {
              // Separate custom scenario from others
              const customScenario = scenarios.find(s => s.id === 'custom');
              const otherScenarios = scenarios.filter(s => s.id !== 'custom');
              
              // Group scenarios by category
              const groupedScenarios = otherScenarios.reduce((acc, scenario) => {
                const category = scenario.category || 'Other';
                if (!acc[category]) {
                  acc[category] = [];
                }
                acc[category].push(scenario);
                return acc;
              }, {} as Record<string, typeof otherScenarios>);

              // Get categories in alphabetical order
              const categories = Object.keys(groupedScenarios).sort((a, b) => a.localeCompare(b));
              
              // Get scenarios to display - all if no category selected, or filtered by category
              const scenariosToDisplay = selectedCategory 
                ? groupedScenarios[selectedCategory] || []
                : otherScenarios;

              return (
                <div className="w-full max-w-full" style={{ maxWidth: '100vw', boxSizing: 'border-box' }}>
                  {/* Custom Scenario - Always at top */}
                  {customScenario && (
                    <div className="w-full mb-4 sm:mb-3 max-w-full" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                      <Card
                        className={cn(
                          "group relative border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white transition-all duration-300 w-full",
                          canCreateNegotiation 
                            ? "hover:border-purple-400 hover:shadow-xl cursor-pointer"
                            : "opacity-60 cursor-not-allowed"
                        )}
                        style={{ maxWidth: '100%', boxSizing: 'border-box', minWidth: 0 }}
                        onClick={() => {
                          if (canCreateNegotiation) {
                            handleStartNegotiation(selectedJob, customScenario.id);
                          } else {
                            // Ensure rateLimitInfo is set for the modal if clicking locked card
                            if (!rateLimitInfo && !isPremium) {
                              const now = new Date();
                              const weekEnd = new Date(now);
                              weekEnd.setDate(now.getDate() + (7 - ((now.getDay() + 6) % 7))); // Next Monday
                              weekEnd.setHours(0, 0, 0, 0);
                              setRateLimitInfo({
                                used: 1,
                                limit: 1,
                                windowEnd: weekEnd.toISOString(),
                              });
                            }
                            setShowPremiumModal(true);
                          }
                        }}
                      >
                        {/* Lock icon overlay for locked custom scenario - clickable */}
                        {!canCreateNegotiation && (
                          <div 
                            className="absolute top-3 right-3 z-10 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Ensure rateLimitInfo is set for the modal if clicking lock
                              if (!rateLimitInfo && !isPremium) {
                                const now = new Date();
                                const weekEnd = new Date(now);
                                weekEnd.setDate(now.getDate() + (7 - ((now.getDay() + 6) % 7))); // Next Monday
                                weekEnd.setHours(0, 0, 0, 0);
                                setRateLimitInfo({
                                  used: 1,
                                  limit: 1,
                                  windowEnd: weekEnd.toISOString(),
                                });
                              }
                              setShowPremiumModal(true);
                            }}
                            title="Subscription limit reached - Click to upgrade"
                          >
                            <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200 hover:bg-gray-50 transition-colors">
                              <Lock className="h-5 w-5 text-slate-700" />
                            </div>
                          </div>
                        )}
                        <CardHeader className="relative pb-3 sm:pb-3 pl-4 pr-4 sm:pl-3 sm:pr-3 pt-4 sm:pt-4" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                          <CardTitle 
                            className="text-base sm:text-lg font-bold text-gray-900 mb-2 leading-tight" 
                            style={{ 
                              wordWrap: 'break-word', 
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              maxWidth: '100%'
                            }}
                          >
                            {customScenario.label}
                          </CardTitle>
                          <CardDescription 
                            className="text-xs sm:text-xs text-gray-700 leading-relaxed" 
                            style={{ 
                              wordWrap: 'break-word', 
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              maxWidth: '100%'
                            }}
                          >
                            {customScenario.site_label}
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    </div>
                  )}

                  {/* Category Filter */}
                  {categories.length > 0 && (
                    <div className="mb-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className={cn(
                            "px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0",
                            selectedCategory === null
                              ? 'bg-[#4f9ecb] text-white shadow-md'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-[#4f9ecb] hover:text-[#4f9ecb]'
                          )}
                        >
                          All
                          <span className="ml-2 text-xs opacity-75">
                            ({otherScenarios.length})
                          </span>
                        </button>
                        {categories.map((category) => {
                          const categoryColor = getCategoryColor(category);
                          return (
                            <button
                              key={category}
                              onClick={() => setSelectedCategory(category)}
                              className={cn(
                                "px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0",
                                selectedCategory === category
                                  ? 'text-white shadow-md'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:text-white'
                              )}
                              style={
                                selectedCategory === category
                                  ? { backgroundColor: categoryColor }
                                  : { 
                                      borderColor: categoryColor,
                                    } as React.CSSProperties
                              }
                              onMouseEnter={(e) => {
                                if (selectedCategory !== category) {
                                  e.currentTarget.style.backgroundColor = categoryColor;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedCategory !== category) {
                                  e.currentTarget.style.backgroundColor = '';
                                }
                              }}
                            >
                              {category}
                              <span className="ml-2 text-xs opacity-75">
                                ({groupedScenarios[category].length})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Scenarios List */}
                  {scenariosToDisplay.length > 0 && (
                    <div className="w-full max-w-full space-y-3 sm:space-y-3" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                      {scenariosToDisplay.map((scenario) => {
                        const category = scenario.category || 'Other';
                        const categoryColor = getCategoryColor(category);
                        return (
                          <Card
                            key={scenario.id}
                            className={cn(
                              "group relative border border-gray-200 transition-all duration-300 bg-white w-full",
                              canCreateNegotiation
                                ? "hover:shadow-xl cursor-pointer"
                                : "opacity-60 cursor-not-allowed"
                            )}
                            style={{ maxWidth: '100%', boxSizing: 'border-box', minWidth: 0 }}
                            onClick={() => {
                              if (canCreateNegotiation) {
                                handleStartNegotiation(selectedJob, scenario.id);
                              } else {
                                // Ensure rateLimitInfo is set for the modal if clicking locked card
                                if (!rateLimitInfo && !isPremium) {
                                  const now = new Date();
                                  const weekEnd = new Date(now);
                                  weekEnd.setDate(now.getDate() + (7 - ((now.getDay() + 6) % 7))); // Next Monday
                                  weekEnd.setHours(0, 0, 0, 0);
                                  setRateLimitInfo({
                                    used: 1,
                                    limit: 1,
                                    windowEnd: weekEnd.toISOString(),
                                  });
                                }
                                setShowPremiumModal(true);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (canCreateNegotiation) {
                                e.currentTarget.style.borderColor = categoryColor + '60';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '';
                            }}
                          >
                            {/* Category-colored accent bar */}
                            <div 
                              className="absolute left-0 top-0 bottom-0 w-1 group-hover:w-1.5 transition-all"
                              style={{ 
                                background: `linear-gradient(to bottom, ${categoryColor}, ${categoryColor}dd)`
                              }}
                            ></div>
                            
                            {/* Lock icon overlay for locked scenarios - clickable */}
                            {!canCreateNegotiation && (
                              <div 
                                className="absolute top-3 right-3 z-10 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowPremiumModal(true);
                                }}
                                title="Subscription limit reached - Click to upgrade"
                              >
                                <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200 hover:bg-gray-50 transition-colors">
                                  <Lock className="h-5 w-5 text-slate-700" />
                                </div>
                              </div>
                            )}
                            
                            <CardHeader className="relative pb-2 sm:pb-2 pl-4 pr-4 sm:pl-3 sm:pr-3 pt-4 sm:pt-4" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                              <CardTitle 
                                className="text-sm sm:text-base font-bold text-gray-900 mb-2 leading-tight" 
                                style={{ 
                                  wordWrap: 'break-word', 
                                  overflowWrap: 'anywhere', 
                                  wordBreak: 'break-word',
                                  maxWidth: '100%'
                                }}
                              >
                                {scenario.label}
                              </CardTitle>
                              <CardDescription 
                                className="text-xs sm:text-xs text-gray-600 leading-relaxed" 
                                style={{ 
                                  wordWrap: 'break-word', 
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  maxWidth: '100%'
                                }}
                              >
                                {scenario.site_label}
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-0 pb-3 sm:pb-3 pl-4 pr-4 sm:pl-3 sm:pr-3">
                              <span className="text-xs text-gray-500 font-medium">
                                Practice Scenario
                              </span>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

      </div>
      {/* Premium lock modal */}
      <PremiumLockModal
        open={showPremiumModal}
        onOpenChange={(open) => {
          if (!open) setRateLimitInfo(null);
          setShowPremiumModal(open);
        }}
        featureLabel="Salary Negotiation"
        description={
          rateLimitInfo
            ? `You've used your weekly salary negotiation (${rateLimitInfo.used}/${rateLimitInfo.limit}). ${
                rateLimitInfo.windowEnd
                  ? `Your limit resets on ${new Date(rateLimitInfo.windowEnd).toLocaleDateString()}. `
                  : ''
              }Upgrade to Premium for unlimited practice sessions.`
            : "Salary negotiation practice is available once per week for free users. Upgrade to Premium for unlimited practice sessions."
        }
        ctaLabel="Subscribe now"
      />
    </DashboardLayout>
  );
}
